import * as React from "react";
import { Card, MessageBar, MessageBarBody, Spinner, Subtitle1, Switch, Text } from "@fluentui/react-components";
import { diktatSchalten, diktatStatus, type DiktatStatus } from "../../services/diktatService";

/**
 * Admin: Diktieren (Spracheingabe) für den Mandanten ein- oder ausschalten.
 *
 * Der Mikrofon-Knopf in Notizen, Kommentaren und Mails erscheint nur, wenn
 * BEIDES stimmt: Der Server hat einen Dienst eingerichtet (heute Mistral
 * Voxtral über `MISTRAL_API_KEY`) und hier ist es eingeschaltet. Standard: aus.
 */
export const DiktatSection: React.FC = () => {
    const [status, setStatus] = React.useState<DiktatStatus | null>(null);
    const [speichert, setSpeichert] = React.useState(false);
    const [fehler, setFehler] = React.useState<string | null>(null);

    React.useEffect(() => {
        let aktiv = true;
        void diktatStatus().then((s) => aktiv && setStatus(s));
        return () => {
            aktiv = false;
        };
    }, []);

    if (!status) {
        return (
            <Card style={{ padding: 24 }}>
                <Spinner label="Einstellung wird geladen …" />
            </Card>
        );
    }

    const umschalten = async (an: boolean) => {
        setSpeichert(true);
        setFehler(null);
        try {
            setStatus(await diktatSchalten(an));
        } catch {
            setFehler("Die Einstellung konnte nicht gespeichert werden. Bitte noch einmal versuchen.");
        } finally {
            setSpeichert(false);
        }
    };

    return (
        <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
                <Subtitle1>Diktieren</Subtitle1>
                <Text block style={{ marginTop: 6, color: "#6b5a66" }}>
                    Mit dem Mikrofon-Knopf in Notizen, Kommentaren und Mails können alle Mitarbeiter
                    Text sprechen statt tippen. Die Aufnahme wird zum Umwandeln an den Sprachdienst
                    geschickt (Mistral, EU) und nirgends gespeichert — zurück kommt nur der Text.
                </Text>
            </div>

            {!status.verfuegbar ? (
                <MessageBar intent="warning">
                    <MessageBarBody>
                        Auf dem Server ist noch kein Sprachdienst eingerichtet. Der Schalter wirkt erst,
                        wenn die IT ihn eingerichtet hat.
                    </MessageBarBody>
                </MessageBar>
            ) : null}
            {fehler ? (
                <MessageBar intent="error">
                    <MessageBarBody>{fehler}</MessageBarBody>
                </MessageBar>
            ) : null}

            <Switch
                checked={status.eingeschaltet}
                disabled={speichert}
                label={status.eingeschaltet ? "Diktieren ist eingeschaltet" : "Diktieren ist ausgeschaltet"}
                onChange={(_, d) => void umschalten(d.checked)}
            />
        </Card>
    );
};
